"use client";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import { Button } from "@/components/ui/button.tsx";
import PATHS from "@/routes/paths.ts";
import envs from "@/envs.ts";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";

interface ConfirmationScreenProps {
  invitationId: string;
}

// Shake animation variants
const shakeAnimation = {
  shake: {
    x: [0, -1.5, 1.5, -2, 2, 0],
    transition: { duration: 0.4, ease: "easeInOut" },
  },
};

export function ConfirmationScreen({ invitationId }: ConfirmationScreenProps) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation(["workspaces", "common"]);
  const invitationLink = `${envs.VITE_SITE_URL}${PATHS.STUDENT.WORKSPACES.JOIN}/${invitationId}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(invitationLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareInvitation = () => {
    if (navigator.share) {
      navigator
        .share({
          title: t("workspaces:shareInvitationTitle"),
          text: t("workspaces:shareInvitationText"),
          url: invitationLink,
        })
        .catch((error) => console.log("Error sharing:", error));
    } else {
      copyToClipboard();
    }
  };

  return (
    <Card className="border-opacity-50 mx-auto flex h-full w-full flex-col border-none bg-transparent">
      <CardHeader className="flex-shrink-0 space-y-2 pb-4 text-center max-sm:pb-3">
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-green-500/20 to-blue-600/20 backdrop-blur-sm max-sm:h-12 max-sm:w-12"
          initial={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <Icon className="text-green-500" height="2rem" icon="tabler:check" width="2rem" />
        </motion.div>
        <CardTitle className="text-accent-foreground text-xl leading-tight font-bold max-sm:text-lg">
          {t("workspaces:invitationReady")}
        </CardTitle>
        <CardDescription className="text-accent-foreground/70 text-sm max-sm:text-xs">
          {t("workspaces:invitationReadyDescription")}
        </CardDescription>
      </CardHeader>

      <CardContent className="min-h-0 flex-1 space-y-4 px-0 max-sm:space-y-3">
        <motion.div
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-600/10 p-4 backdrop-blur-sm max-sm:p-3"
          initial={{ opacity: 0, scale: 0.95 }}
          transition={{ delay: 0.2, duration: 0.3, ease: "easeOut" }}
        >
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500/20">
              <Icon className="text-blue-500" height="1rem" icon="tabler:link" width="1rem" />
            </div>
            <p className="text-accent-foreground text-sm font-semibold max-sm:text-xs">
              {t("workspaces:invitationLink")}
            </p>
          </div>
          <div className="bg-background/80 flex items-center gap-3 rounded-xl p-3 backdrop-blur-sm max-sm:p-2">
            <ScrollArea className="h-6 max-w-[75vw] flex-1 max-[400px]:max-w-[60vw] max-sm:h-8">
              <code className="text-accent-foreground/90 font-mono text-xs max-sm:text-xs">
                {invitationLink}
              </code>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        </motion.div>
      </CardContent>

      <CardFooter className="flex-shrink-0 p-0 pt-4 max-sm:pt-3">
        <div className="flex w-full gap-3 max-sm:flex-col max-sm:gap-3">
          <Button className="w-full" variant={"foreground"} onClick={copyToClipboard}>
            <motion.div
              key={copied ? "check-btn" : "copy-btn"}
              animate={copied ? "shake" : ""}
              className="mr-2 flex items-center"
              transition={{ type: "spring", stiffness: 300, damping: 15 }}
              variants={shakeAnimation}
            >
              {copied ? (
                <Icon height="1rem" icon="lucide:check" width="1rem" />
              ) : (
                <Icon height="1rem" icon="lucide:copy" width="1rem" />
              )}
            </motion.div>
            <span className="text-sm font-medium max-sm:text-xs">{t("workspaces:copyLink")}</span>
          </Button>
          <Button className="w-full" variant={"outline"} onClick={shareInvitation}>
            <Icon className="mr-2" height="1rem" icon="lucide:share-2" width="1rem" />
            <span className="text-sm font-medium max-sm:text-xs">
              {t("workspaces:shareInvitation")}
            </span>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
